import React, { useMemo, useState } from "react";
import { FieldError } from "react-hook-form";
import { Calendar, Day, DayRange } from "@hassanmojab/react-modern-calendar-datepicker";
import { mdiCalendar, mdiClockOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import { getClassByVariant } from "helpers/getClassByVariant";
import styles from "./style.module.scss";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import "./_style.scss";

interface IProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string | React.ReactNode;
  className?: string;
  labelClass?: string;
  inputClass?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: FieldError;
  color?: "primary" | "secondary";
  isRange?: boolean;
  required?: boolean;
  showTimePicker?: boolean;
}

interface ICalendarFooter {
  onClose: VoidFunction;
}

const dayString = ({ day, month, year }: Day) =>
  dayjs(`${month}/${day}/${year}`).format("MM/DD/YYYY");

const timeString = (timeValue: { hours: number; minutes: number }) =>
  `${timeValue.hours.toString().padStart(2, "0")}:${timeValue.minutes.toString().padStart(2, "0")}`;

function CalendarFooter({ onClose }: Readonly<ICalendarFooter>) {
  return (
    <div className="flex flex-1 justify-between *:h-10 *:w-24 *:text-sm m-6 mx-10 -mt-6">
      <button type="button" onClick={onClose}>
        Cancel
      </button>
      <Button type="button" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}

function RangeCalendar({
  dateValue,
  onChange,
  onSubmit
}: Readonly<{
  dateValue: any;
  onChange: (value: any) => void;
  onSubmit: VoidFunction;
}>) {
  return (
    <Calendar
      value={dateValue}
      onChange={onChange}
      colorPrimary="rgb(var(--primary))"
      colorPrimaryLight="rgb(var(--primary))"
      slideAnimationDuration="0.5s"
      calendarTodayClassName="!text-primary underline underline-offset-4"
      renderFooter={() => <CalendarFooter onClose={onSubmit} />}
    />
  );
}

function SingleDateCalendar({
  dayValue,
  onChange,
  onSubmit
}: Readonly<{
  dayValue: any;
  onChange: (value: any) => void;
  onSubmit: VoidFunction;
}>) {
  return (
    <Calendar
      value={dayValue}
      onChange={onChange}
      colorPrimary="rgb(var(--primary))"
      colorPrimaryLight="rgb(var(--primary))"
      slideAnimationDuration="0.5s"
      calendarTodayClassName="!text-primary underline underline-offset-4"
      renderFooter={() => <CalendarFooter onClose={onSubmit} />}
    />
  );
}

export default function DatePicker({
  value,
  onChange,
  label,
  placeholder,
  color = "primary",
  className,
  labelClass,
  error,
  inputClass,
  disabled = false,
  isRange = false,
  required = false,
  showTimePicker = false
}: Readonly<IProps>) {
  const inputDate = value ? dayjs(value) : null;
  const [isOpen, toggleModal] = useToggle(false);
  const [dayValue, setDayValue] = useState(
    inputDate
      ? {
          day: inputDate.date(),
          month: inputDate.month() + 1,
          year: inputDate.year()
        }
      : undefined
  );
  const [dateValue, setDateValue] = useState({
    from: null,
    to: null
  });

  const [timeValue, setTimeValue] = useState({ hours: dayjs().hour(), minutes: dayjs().minute() }); // Default time or restored from value

  const inputValue = useMemo(() => {
    if (isRange) {
      const dayRange = dateValue as DayRange;
      if (dayRange.from && dayRange.to) {
        return showTimePicker
          ? `${dayString(dayRange.from)} ${timeString(timeValue)} to ${dayString(dayRange.to)} ${timeString(timeValue)}`
          : `${dayString(dayRange.from)} to ${dayString(dayRange.to)}`;
      }
    } else if (dayValue) {
      return showTimePicker
        ? `${dayString(dayValue)} ${timeString(timeValue)}`
        : dayString(dayValue);
    }
    return "";
  }, [dateValue, dayValue, timeValue]);

  const onSubmit = () => {
    toggleModal();
    onChange && onChange(inputValue);
  };

  return (
    <div className={clsx(styles.smartDatePickerWrapper, className)}>
      {label && (
        <label
          className={clsx(
            styles.smartDatePickerLabel,
            getClassByVariant(color)?.label,
            labelClass
          )}>
          {required ? "* " : ""}
          {label}
        </label>
      )}

      <Input
        value={value}
        onChange={(event) => onChange && onChange(event.target.value)}
        onClick={toggleModal}
        className={clsx(
          styles.smartDatePickerInput,
          getClassByVariant(color)?.input,
          !!error && "!border-b-error"
        )}
        inputClass={inputClass}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
      />
      <Icon
        path={mdiCalendar}
        className={clsx(styles.calendarIcon, showTimePicker ? "!right-10" : "!right-3")}
      />
      {showTimePicker ? <Icon path={mdiClockOutline} className={styles.clockIcon} /> : null}

      {error && <span className="text-sm leading-5 text-error">{error.message}</span>}
      <Modal
        isOpen={isOpen}
        closeModal={toggleModal}
        wrapperClass="!m-auto !p-0"
        dialogClass="desktop:!m-auto"
        closeClass="!hidden">
        <div>
          {isRange ? (
            <RangeCalendar dateValue={dateValue} onChange={setDateValue} onSubmit={onSubmit} />
          ) : (
            <SingleDateCalendar dayValue={dayValue} onChange={setDayValue} onSubmit={onSubmit} />
          )}

          {showTimePicker ? (
            <div className="flex justify-center mt-1">
              <Input
                type="time"
                className="w-full max-w-xs mobile:text-sm !text-text" // form-input leading-normal bg-foreground rounded-base border border-transparent focus:outline-none focus:border-primary shadow-md px-3 py-2
                inputClass="text-text !bg-foreground !py-3"
                value={`${timeValue.hours}:${timeValue.minutes}`}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(":");
                  setTimeValue({ hours: parseInt(hours), minutes: parseInt(minutes) });
                }}
              />
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
